import React, { useMemo } from 'react';
import {
  ChevronDown, ChevronUp, AlertTriangle, Flame,
  TrendingUp, TrendingDown,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';

// Constantes UI partagées (mémorisées globalement pour éviter les ré-créations à chaque render)
const ELEC_CHART_MARGIN = { top: 5, right: 30, left: 120, bottom: 5 };
const GAZ_CHART_MARGIN = { top: 5, right: 30, left: 80, bottom: 5 };
const CARD_BOX_STYLE = { marginTop: '24px', background: '#F8FAFC', borderRadius: '12px', padding: '20px' };
const CARD_HEADING_STYLE = { color: '#475569', marginBottom: '16px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' };
const CHART_HEIGHT_STYLE = { height: '200px' };
const TOOLTIP_LABEL_STYLE = { fontWeight: 'bold' };

const OPTION_TARIFAIRE_LABELS = {
  CU: 'CU - Courte Utilisation',
  MU: 'MU - Moyenne Utilisation',
  LU: 'LU - Longue Utilisation',
};

const DOMAINE_LABELS = {
  HTA: 'HTA (250 kVA - 10 MW)',
  BT_SUP36: 'BT > 36 kVA (36 - 250 kVA)',
  BT_INF36: 'BT ≤ 36 kVA',
};

/**
 * Section "Acheminement" (TURPE Élec ou ATRD Gaz) - Mode Pro uniquement.
 * Le rendu est conditionné par energyType.
 */
const AcheminementSection = ({
  energyType,
  showAcheminement,
  setShowAcheminement,
  formatNumber,
  // Élec
  optionTarifaire,
  puissanceSouscrite,
  segmentElec,
  totalConsoMWh,
  acheminementActuelCalc,
  acheminementFuturCalc,
  // Gaz
  segmentGaz,
  segmentGazFutur,
  setSegmentGazFutur,
  capaciteJournaliereT4Futur,
  setCapaciteJournaliereT4Futur,
  niveauCapaciteT4Futur,
  setNiveauCapaciteT4Futur,
  consoGazMWh,
  ATRD,
  capaciteT4,
  capaciteT4Fut,
  atrdActuel,
  atrdFutur,
}) => {
  // Mémorisation des datasets de chart (évite de re-créer ces objets à chaque render)
  const elecChartData = useMemo(() => [
    {
      name: 'Coût TURPE Actuel',
      total: acheminementActuelCalc.total,
      gestion: acheminementActuelCalc.gestion + acheminementActuelCalc.comptage,
      puissance: acheminementActuelCalc.partFixePuissance,
      energie: acheminementActuelCalc.partVariable,
      cta: acheminementActuelCalc.cta,
    },
    {
      name: 'Coût TURPE Futur',
      total: acheminementFuturCalc.total,
      gestion: acheminementFuturCalc.gestion + acheminementFuturCalc.comptage,
      puissance: acheminementFuturCalc.partFixePuissance,
      energie: acheminementFuturCalc.partVariable,
      cta: acheminementFuturCalc.cta,
    },
  ], [acheminementActuelCalc, acheminementFuturCalc]);

  const gazChartData = useMemo(() => [
    {
      name: `Actuel (${segmentGaz})`,
      total: atrdActuel.totalHT,
      abonnement: atrdActuel.partFixe,
      variable: atrdActuel.partVariable,
      cta: atrdActuel.cta,
    },
    {
      name: `Futur (${segmentGazFutur})`,
      total: atrdFutur.totalHT,
      abonnement: atrdFutur.partFixe,
      variable: atrdFutur.partVariable,
      cta: atrdFutur.cta,
    },
  ], [segmentGaz, segmentGazFutur, atrdActuel, atrdFutur]);

  const tooltipFormatter = useMemo(
    () => (value) => `${formatNumber(value)} €`,
    [formatNumber]
  );

  const elecTickFormatter = useMemo(() => (v) => `${(v / 1000).toFixed(1)}k €`, []);
  const gazTickFormatter = useMemo(() => (v) => `${(v / 1000).toFixed(0)}k €`, []);

  if (energyType === 'electricity') {
    return (
      <section className="card optional-section" data-testid="acheminement-section">
        <button
          className="section-toggle"
          onClick={() => setShowAcheminement(!showAcheminement)}
          data-testid="acheminement-toggle"
        >
          <h2 className="section-title">
            Acheminement (TURPE + CTA) <span className="annual-badge">Budget Annuel</span>
          </h2>
          {showAcheminement ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        {showAcheminement && (
          <div className="optional-content">
            <div className="accise-info">
              <p className="accise-formula">
                <strong>TURPE 7 HTA-BT :</strong> CG + CC + CS (Σ bi×P + Σ ci×E) + CTA (15% sur CG+CC+CS puissance)
              </p>

              {(!optionTarifaire || puissanceSouscrite <= 0) && (
                <div className="warning-box" style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <p style={{ color: '#92400E', margin: 0 }}>
                    <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                    Veuillez renseigner la <strong>Puissance Souscrite</strong> et l'<strong>Option Tarifaire</strong> dans "Informations Générales" pour calculer l'acheminement.
                  </p>
                </div>
              )}

              {optionTarifaire && puissanceSouscrite > 0 && (
                <>
                  <div className="accise-details">
                    {segmentElec && (
                      <div className="accise-detail-item">
                        <span className="detail-label">Segment Client</span>
                        <span className="detail-value">{segmentElec}</span>
                      </div>
                    )}
                    <div className="accise-detail-item">
                      <span className="detail-label">Option Tarifaire</span>
                      <span className="detail-value">
                        {OPTION_TARIFAIRE_LABELS[optionTarifaire] || optionTarifaire}
                      </span>
                    </div>
                    <div className="accise-detail-item">
                      <span className="detail-label">Domaine de tension</span>
                      <span className="detail-value">
                        {DOMAINE_LABELS[acheminementActuelCalc.domaine] || DOMAINE_LABELS.BT_INF36}
                      </span>
                    </div>
                    <div className="accise-detail-item">
                      <span className="detail-label">Consommation totale</span>
                      <span className="detail-value">{formatNumber(totalConsoMWh)} MWh</span>
                    </div>
                  </div>

                  {/* Tableau comparatif Actuel vs Futur */}
                  <div className="acheminement-comparison" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '20px' }}>
                    <AcheminementElecBox
                      title="Budget Actuel"
                      colorTitle="#3B82F6"
                      bgBox="#F1F5F9"
                      borderColor="#CBD5E1"
                      data={acheminementActuelCalc}
                      formatNumber={formatNumber}
                      resultBg=""
                    />
                    <AcheminementElecBox
                      title="Budget Futur"
                      colorTitle="#22C55E"
                      bgBox="#ECFDF5"
                      borderColor="#A7F3D0"
                      data={acheminementFuturCalc}
                      formatNumber={formatNumber}
                      resultBg="#D1FAE5"
                    />
                  </div>

                  {/* Graphique comparatif Électricité */}
                  {totalConsoMWh > 0 && (
                    <div style={CARD_BOX_STYLE}>
                      <h4 style={CARD_HEADING_STYLE}>
                        Comparatif visuel Acheminement Électricité
                      </h4>
                      <div style={CHART_HEIGHT_STYLE}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={elecChartData}
                            layout="vertical"
                            margin={ELEC_CHART_MARGIN}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tickFormatter={elecTickFormatter} />
                            <YAxis type="category" dataKey="name" />
                            <Tooltip
                              formatter={tooltipFormatter}
                              labelStyle={TOOLTIP_LABEL_STYLE}
                            />
                            <Legend />
                            <Bar dataKey="gestion" stackId="a" fill="#3B82F6" name="Gestion+Comptage" />
                            <Bar dataKey="puissance" stackId="a" fill="#60A5FA" name="Part Fixe" />
                            <Bar dataKey="energie" stackId="a" fill="#93C5FD" name="Part Énergie" />
                            <Bar dataKey="cta" stackId="a" fill="#BFDBFE" name="CTA" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {acheminementActuelCalc.total !== acheminementFuturCalc.total && (
                        <div style={{
                          marginTop: '16px',
                          textAlign: 'center',
                          padding: '12px',
                          background: acheminementFuturCalc.total < acheminementActuelCalc.total ? '#D1FAE5' : '#FEE2E2',
                          borderRadius: '8px',
                        }}>
                          <strong style={{ color: acheminementFuturCalc.total < acheminementActuelCalc.total ? '#166534' : '#991B1B', fontSize: '1.1rem' }}>
                            {acheminementFuturCalc.total < acheminementActuelCalc.total ? '↓' : '↑'} {formatNumber(Math.abs(((acheminementFuturCalc.total - acheminementActuelCalc.total) / acheminementActuelCalc.total) * 100))}%
                            {acheminementFuturCalc.total < acheminementActuelCalc.total ? " d'économie" : ' de surcoût'} sur l'acheminement HT
                          </strong>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="accise-disclaimer" style={{ marginTop: '16px' }}>
                    Calcul basé sur les coefficients officiels TURPE 7 HTA-BT (Délibération CRE N°2025-78 du 13/03/2025, applicable au 1er août 2025). CTA à 15% (sur CG+CC+CS puissance). TVA à 20%.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </section>
    );
  }

  // GAZ
  return (
    <section className="card optional-section" data-testid="acheminement-gaz-section">
      <button
        className="section-toggle"
        onClick={() => setShowAcheminement(!showAcheminement)}
        data-testid="acheminement-gaz-toggle"
      >
        <h2 className="section-title">
          <Flame size={20} className="section-icon" style={{ color: '#F97316' }} />
          Acheminement Gaz (ATRD + CTA) <span className="annual-badge">Budget Annuel</span>
        </h2>
        {showAcheminement ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {showAcheminement && (
        <div className="optional-content">
          <div className="accise-info">
            <p className="accise-formula">
              <strong>ATRD7 (01/07/2025 – 30/06/2026) :</strong> Abonnement + Part Variable (Conso × Coeff.) + Part Capacité (T4) + CTA (20,80%)
            </p>

            <div className="accise-details">
              <div className="accise-detail-item">
                <span className="detail-label">Consommation</span>
                <span className="detail-value">{formatNumber(consoGazMWh)} MWh</span>
              </div>
            </div>

            {/* Comparatif Actuel vs Futur */}
            <div className="acheminement-comparison" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '20px' }}>
              {/* Budget Actuel */}
              <div className="acheminement-box" style={{ background: '#FFF7ED', borderRadius: '12px', padding: '16px' }}>
                <h4 style={{ color: '#F97316', marginBottom: '12px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Budget Actuel - Segment {segmentGaz}
                </h4>
                <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '12px' }}>
                  <span>Abonnement: <strong>{formatNumber(ATRD.abonnement[segmentGaz])} €/an</strong></span>
                  <span style={{ marginLeft: '16px' }}>Coeff: <strong>{formatNumber(ATRD.variable[segmentGaz])} €/MWh</strong></span>
                </div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span>Part Fixe (Abonnement)</span>
                    <span>{formatNumber(atrdActuel.partFixe)} €</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span>Part Variable ({formatNumber(consoGazMWh)} × {formatNumber(ATRD.variable[segmentGaz])})</span>
                    <span>{formatNumber(atrdActuel.partVariable)} €</span>
                  </div>
                  {segmentGaz === 'T4' && capaciteT4 > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span>Part Capacité</span>
                      <span>{formatNumber(atrdActuel.partCapacite)} €</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', borderTop: '1px solid #FDBA74', paddingTop: '4px' }}>
                    <span><strong>ATRD HT</strong></span>
                    <span><strong>{formatNumber(atrdActuel.atrdHT)} €</strong></span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span>CTA (20,80%)</span>
                    <span>{formatNumber(atrdActuel.cta)} €</span>
                  </div>
                </div>
                <div className="accise-result auto" style={{ marginTop: '12px', background: '#FFEDD5' }}>
                  <span className="result-label">Total Actuel</span>
                  <span className="result-value">{formatNumber(atrdActuel.totalHT)} €/an</span>
                </div>
              </div>

              {/* Budget Futur */}
              <div className="acheminement-box" style={{ background: '#ECFDF5', borderRadius: '12px', padding: '16px' }}>
                <h4 style={{ color: '#22C55E', marginBottom: '12px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Budget Futur - Segment
                  <select
                    value={segmentGazFutur}
                    onChange={(e) => setSegmentGazFutur(e.target.value)}
                    style={{
                      marginLeft: '8px',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      border: '2px solid #22C55E',
                      background: '#fff',
                      fontWeight: 'bold',
                      color: '#22C55E',
                      cursor: 'pointer',
                    }}
                    data-testid="segment-gaz-futur-select"
                  >
                    <option value="T1">T1</option>
                    <option value="T2">T2</option>
                    <option value="T3">T3</option>
                    <option value="T4">T4</option>
                  </select>
                </h4>

                {/* Options supplémentaires pour T4 futur */}
                {segmentGazFutur === 'T4' && (
                  <div style={{ marginBottom: '12px', padding: '8px', background: '#D1FAE5', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#059669' }}>Capacité (MWh/j)</label>
                        <input
                          type="number"
                          value={capaciteJournaliereT4Futur}
                          onChange={(e) => setCapaciteJournaliereT4Futur(e.target.value)}
                          placeholder="0"
                          style={{ width: '80px', padding: '4px', borderRadius: '4px', border: '1px solid #A7F3D0', marginLeft: '4px' }}
                          data-testid="capacite-t4-futur-input"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#059669' }}>Niveau</label>
                        <select
                          value={niveauCapaciteT4Futur}
                          onChange={(e) => setNiveauCapaciteT4Futur(e.target.value)}
                          style={{ padding: '4px', borderRadius: '4px', border: '1px solid #A7F3D0', marginLeft: '4px' }}
                          data-testid="niveau-capacite-futur-select"
                        >
                          <option value="T4_low">Bas (144€)</option>
                          <option value="T4_high">Haut (288€)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '12px' }}>
                  <span>Abonnement: <strong>{formatNumber(ATRD.abonnement[segmentGazFutur])} €/an</strong></span>
                  <span style={{ marginLeft: '16px' }}>Coeff: <strong>{formatNumber(ATRD.variable[segmentGazFutur])} €/MWh</strong></span>
                </div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span>Part Fixe (Abonnement)</span>
                    <span>{formatNumber(atrdFutur.partFixe)} €</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span>Part Variable ({formatNumber(consoGazMWh)} × {formatNumber(ATRD.variable[segmentGazFutur])})</span>
                    <span>{formatNumber(atrdFutur.partVariable)} €</span>
                  </div>
                  {segmentGazFutur === 'T4' && capaciteT4Fut > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span>Part Capacité</span>
                      <span>{formatNumber(atrdFutur.partCapacite)} €</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', borderTop: '1px solid #6EE7B7', paddingTop: '4px' }}>
                    <span><strong>ATRD HT</strong></span>
                    <span><strong>{formatNumber(atrdFutur.atrdHT)} €</strong></span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span>CTA (20,80%)</span>
                    <span>{formatNumber(atrdFutur.cta)} €</span>
                  </div>
                </div>
                <div className="accise-result auto" style={{ marginTop: '12px', background: '#D1FAE5' }}>
                  <span className="result-label">Total Futur</span>
                  <span className="result-value">{formatNumber(atrdFutur.totalHT)} €/an</span>
                </div>
              </div>
            </div>

            {/* Écart entre les deux */}
            {segmentGaz !== segmentGazFutur && (
              <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                background: atrdFutur.totalHT < atrdActuel.totalHT ? '#DCFCE7' : '#FEF2F2',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ fontWeight: '600', color: atrdFutur.totalHT < atrdActuel.totalHT ? '#166534' : '#991B1B' }}>
                  {atrdFutur.totalHT < atrdActuel.totalHT ? (
                    <><TrendingDown size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />Économie avec passage {segmentGaz} → {segmentGazFutur}</>
                  ) : (
                    <><TrendingUp size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />Surcoût avec passage {segmentGaz} → {segmentGazFutur}</>
                  )}
                </span>
                <span style={{
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  color: atrdFutur.totalHT < atrdActuel.totalHT ? '#166534' : '#991B1B',
                }}>
                  {atrdFutur.totalHT < atrdActuel.totalHT ? '-' : '+'}{formatNumber(Math.abs(atrdFutur.totalHT - atrdActuel.totalHT))} €/an
                </span>
              </div>
            )}

            {/* Graphique comparatif des segments */}
            {consoGazMWh > 0 && (
              <div style={CARD_BOX_STYLE}>
                <h4 style={CARD_HEADING_STYLE}>
                  Comparatif visuel des segments
                </h4>
                <div style={CHART_HEIGHT_STYLE}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={gazChartData}
                      layout="vertical"
                      margin={GAZ_CHART_MARGIN}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={gazTickFormatter} />
                      <YAxis type="category" dataKey="name" />
                      <Tooltip
                        formatter={tooltipFormatter}
                        labelStyle={TOOLTIP_LABEL_STYLE}
                      />
                      <Legend />
                      <Bar dataKey="abonnement" stackId="a" fill="#F97316" name="Abonnement" />
                      <Bar dataKey="variable" stackId="a" fill="#FB923C" name="Part Variable" />
                      <Bar dataKey="cta" stackId="a" fill="#FDBA74" name="CTA" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {segmentGaz !== segmentGazFutur && (
                  <div style={{
                    marginTop: '16px',
                    textAlign: 'center',
                    padding: '12px',
                    background: atrdFutur.totalHT < atrdActuel.totalHT ? '#D1FAE5' : '#FEE2E2',
                    borderRadius: '8px',
                  }}>
                    <strong style={{ color: atrdFutur.totalHT < atrdActuel.totalHT ? '#166534' : '#991B1B', fontSize: '1.1rem' }}>
                      {atrdFutur.totalHT < atrdActuel.totalHT ? '↓' : '↑'} {formatNumber(Math.abs(((atrdFutur.totalHT - atrdActuel.totalHT) / atrdActuel.totalHT) * 100))}%
                      {atrdFutur.totalHT < atrdActuel.totalHT ? " d'économie" : ' de surcoût'} sur l'acheminement
                    </strong>
                  </div>
                )}
              </div>
            )}

            <p className="accise-disclaimer" style={{ marginTop: '16px' }}>
              Coefficients ATRD7 (CRE/GRDF). CTA à 20,80%. TVA (20%) non incluse.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

/**
 * Boîte récapitulative TURPE/CTA pour Élec (Budget Actuel ou Futur).
 */
const AcheminementElecBox = ({ title, colorTitle, bgBox, borderColor, data, formatNumber, resultBg }) => (
  <div className="acheminement-box" style={{ background: bgBox, borderRadius: '12px', padding: '16px' }}>
    <h4 style={{ color: colorTitle, marginBottom: '12px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h4>
    <div style={{ fontSize: '13px', color: '#475569' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span>Gestion (CG)</span>
        <span>{formatNumber(data.gestion)} €</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span>Comptage (CC)</span>
        <span>{formatNumber(data.comptage)} €</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span>Soutirage puissance (CS)</span>
        <span>{formatNumber(data.partFixePuissance)} €</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span>Soutirage énergie (CS)</span>
        <span>{formatNumber(data.partVariable)} €</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', borderTop: `1px solid ${borderColor}`, paddingTop: '4px' }}>
        <span><strong>TURPE HT</strong></span>
        <span><strong>{formatNumber(data.turpeHT)} €</strong></span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span>CTA (15%)</span>
        <span>{formatNumber(data.cta)} €</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', borderTop: `1px solid ${borderColor}`, paddingTop: '4px' }}>
        <span><strong>Total HT</strong></span>
        <span><strong>{formatNumber(data.total)} €</strong></span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span>TVA (20%)</span>
        <span>{formatNumber(data.tva)} €</span>
      </div>
    </div>
    <div className="accise-result auto" style={{ marginTop: '12px', ...(resultBg ? { background: resultBg } : {}) }}>
      <span className="result-label">Total Acheminement HT</span>
      <span className="result-value">{formatNumber(data.total)} €/an</span>
    </div>
  </div>
);

export default AcheminementSection;
